import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/components/common/toast';
import { usePermission } from '@/permissions/usePermission';
import { Tabs, type TabItem } from '@/components/common/Tabs';
import { Select2 } from '@/components/forms/Select2';
import { Select2Async } from '@/components/forms/Select2Async';
import { ConfirmDialog } from '@/components/dialogs/ConfirmDialog';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { StatWidget } from '@/components/widgets/StatWidget';
import { LoginOverviewModal } from '@/features/tenant/loginOverview/LoginOverviewModal';
import { filterDataApi, filterDataPaged } from '@/api/services/filterData';
import { formatNumber } from '@/utils/format';
import { nopStressTestApi } from '@/api/services/nopStressTest';
import { StressNopTable } from './StressNopTable';
import { ScenarioOffcanvas } from './ScenarioOffcanvas';
import { NopStressTestPermissions as P } from './permissions';
import { StressTestTarget, StressTestType, type LoginStopOutResultDto, type ReverseStopOutResultDto, type StressTestConfigurationDto, type StressTestFiltersDto, type StressTestResultDto } from '@/api/types/nopStressTest';
import type { NopSectionDto } from '@/api/types/consolidatedExposure';

const STOP_OUT_ANCHOR = 'nst-stop-out-table';
const scrollToStopOut = () => {
  const el = document.getElementById(STOP_OUT_ANCHOR);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

const money = (v: number | null | undefined, d = 2) => (v == null || Number.isNaN(v) ? '' : formatNumber(v, d));
const SHIFT_MIN = -99;

// Mirrors legacy Runner.validate: snapshot presence + per-type required fields / bounds.
function validateRun(cfg: StressTestConfigurationDto, result: StressTestResultDto | null, toast: ReturnType<typeof useToast>): boolean {
  if (!result?.createdAt) {
    toast.warning('No snapshot available. Please click Take Snapshot first.');
    return false;
  }
  const pct = cfg.parallelShift;
  switch (cfg.type) {
    case StressTestType.ParallelShift:
      if (pct == null) { toast.warning('Shift percentage is required for Parallel Shift.'); return false; }
      if (pct < SHIFT_MIN) { toast.warning(`Shift percentage cannot be less than ${SHIFT_MIN}.`); return false; }
      break;
    case StressTestType.SensitivityAnalysis:
      if (pct == null) { toast.warning('Shift percentage is required for Sensitivity Analysis.'); return false; }
      if (pct < SHIFT_MIN) { toast.warning(`Shift percentage cannot be less than ${SHIFT_MIN}.`); return false; }
      if (!cfg.instrument) { toast.warning('Instrument is required for Sensitivity Analysis.'); return false; }
      break;
    case StressTestType.ScenarioAnalysis:
      if (!cfg.modifiedPrices || !cfg.modifiedPrices.length) {
        toast.warning('At least one modified price is required. Open "Scenario Analysis Config" and set prices first.');
        return false;
      }
      break;
  }
  return true;
}

// Mirrors legacy Runner.buildConfiguration: only the fields relevant to the type are
// sent. Scenario sends modifiedPrices only — never modifiedVolumes.
function buildConfiguration(cfg: StressTestConfigurationDto): StressTestConfigurationDto {
  const base: StressTestConfigurationDto = { type: cfg.type, parallelShift: null, instrument: null, modifiedPrices: null, modifiedVolumes: null, target: cfg.target };
  switch (cfg.type) {
    case StressTestType.ParallelShift:
      base.parallelShift = cfg.parallelShift;
      break;
    case StressTestType.SensitivityAnalysis:
      base.parallelShift = cfg.parallelShift;
      base.instrument = cfg.instrument ?? null;
      break;
    case StressTestType.ScenarioAnalysis:
      base.modifiedPrices = cfg.modifiedPrices ?? null;
      break;
  }
  return base;
}
const emptyFilters = (): StressTestFiltersDto => ({ uniqueId: null, login: null, platformId: null, instrument: null, portfolioId: null, ccyCurrency: false, byUniqueId: false, autoMap: false });
const emptyConfig = (): StressTestConfigurationDto => ({ type: StressTestType.None, parallelShift: null, instrument: null, modifiedPrices: null, modifiedVolumes: null, target: StressTestTarget.Price });

// Renders a "missing symbols/groups" notification ("<message>: A, B, C") as a warning
// alert with each item as its own badge, instead of a line of bare unstyled text.
function MissingNotice({ text }: { text: string }) {
  const sep = text.indexOf(':');
  const intro = sep >= 0 ? text.slice(0, sep + 1) : text;
  const items = sep >= 0 ? text.slice(sep + 1).split(',').map((s) => s.trim()).filter(Boolean) : [];
  return (
    <div className="alert alert-warning d-flex flex-wrap align-items-center gap-2 mb-3" role="alert">
      <i className="fa fa-exclamation-triangle me-1" />
      <span>{intro}</span>
      {items.map((s) => (
        <span key={s} className="badge bg-warning text-dark">
          {s}
        </span>
      ))}
    </div>
  );
}
const TYPE_LABEL: Record<number, string> = { 0: 'N/A', 1: 'Parallel Shift', 2: 'Sensitivity Analysis', 3: 'Scenario Analysis' };
// Snapshot is taken against an unfiltered population (legacy DEFAULT_FILTERS).
const SNAP_FILTERS = (): StressTestFiltersDto => ({ ...emptyFilters() });

export function NopStressTestPage() {
  const toast = useToast();
  const can = usePermission();
  const [active, setActive] = useState('stress');

  const [filters, setFilters] = useState<StressTestFiltersDto>(emptyFilters);
  const [config, setConfig] = useState<StressTestConfigurationDto>(emptyConfig);
  const [result, setResult] = useState<StressTestResultDto | null>(null);
  const [stressed, setStressed] = useState(false); // stats + stop-out table show only after a run
  const [busy, setBusy] = useState(false);
  const [confirmSnap, setConfirmSnap] = useState(false);

  const applyResult = (res: StressTestResultDto) => {
    if (res.errorMessage) toast.error(res.errorMessage);
    setResult(res);
  };

  // Page load: prefer cached snapshot, fall back to a fresh one.
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    nopStressTestApi.getSnapshot(SNAP_FILTERS()).then(applyResult).catch(() => {
      nopStressTestApi.takeSnapshot(SNAP_FILTERS()).then(applyResult).catch(() => {});
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doTakeSnapshot = async () => {
    setBusy(true);
    try {
      setFilters(emptyFilters());
      setConfig(emptyConfig());
      setStressed(false);
      applyResult(await nopStressTestApi.takeSnapshot(SNAP_FILTERS()));
      toast.success('Snapshot taken.');
    } catch { toast.error('Failed to take snapshot.'); } finally { setBusy(false); }
  };

  const run = async (cfg = config, flt = filters) => {
    if (!validateRun(cfg, result, toast)) return;
    setBusy(true);
    try {
      applyResult(await nopStressTestApi.runStressTest({ filters: flt, configuration: buildConfiguration(cfg) }));
      setStressed(cfg.type !== StressTestType.None);
    } catch { toast.error('Failed to run stress test.'); } finally { setBusy(false); }
  };
  const refresh = async (flt = filters) => {
    if (!result?.createdAt) return;
    setBusy(true);
    try { applyResult(await nopStressTestApi.getSnapshot(flt)); setStressed(false); }
    catch { /* keep current */ } finally { setBusy(false); }
  };
  // A filter change re-runs the stress when a type is selected, else refreshes the snapshot.
  const refreshOrRerun = (flt: StressTestFiltersDto) => (config.type !== StressTestType.None ? run(config, flt) : refresh(flt));

  const tabs: TabItem[] = [
    { key: 'stress', title: 'Stress Testing' },
    can(P.reverseStopOut) && { key: 'reverse', title: 'Reverse Stop-Out' },
  ].filter(Boolean) as TabItem[];

  return (
    <div className="nop-stress-test-page" id="nop-stress-test-page">
      <PageHeader title="NOP Stress Test" breadcrumb={['Home', 'NOP Stress Test']} />

      {/* Missing symbols / groups notifications. The backend sends a plain message
          ("…: SYM1, SYM2, …"); show it as a warning alert with each item badged. */}
      {result?.missingSymbolsNotification && <MissingNotice text={result.missingSymbolsNotification} />}
      {result?.missingGroupsNotification && <MissingNotice text={result.missingGroupsNotification} />}

      {/* Intro card: instruction + Take Snapshot + Snapshot Time. */}
      <div className="card custom-card mb-2">
        <div className="card-body py-3">
          <p className="mb-2 text-muted">
            For a stress test, please click on <strong>Take Snapshot</strong> button to take an instant snapshot for the NOP to be able to instantly track the PNL of your traders.
          </p>
          <div className="d-flex flex-wrap align-items-center gap-3">
            <button type="button" className="btn btn-primary" onClick={() => setConfirmSnap(true)} disabled={busy}>
              {busy ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-camera me-1" />}Take Snapshot
            </button>
            <span><span className="text-muted">Snapshot Time:</span> <span className="fw-semibold">{result?.createdAt || 'N/A'}</span></span>
          </div>
        </div>
      </div>

      {/* One card for both tabs; sections split by borders. */}
      <div className="card custom-card">
        <div className="card-header p-0 border-bottom">
          <Tabs tabs={tabs} active={active} onChange={setActive} variant="underline" />
        </div>
        <div className="card-body p-0">
          {active === 'stress' && (
            <StressContent filters={filters} setFilters={setFilters} config={config} setConfig={setConfig} result={result} stressed={stressed} run={run} refreshOrRerun={refreshOrRerun} busy={busy} canCcy={can(P.byCcy)} canUid={can(P.uniqueId)} />
          )}
          {active === 'reverse' && (
            <ReverseStopOut
              snapshotReady={!!result?.createdAt}
              onOpenStressTest={(instrument, shiftPct) => {
                const cfg: StressTestConfigurationDto = { ...emptyConfig(), type: StressTestType.SensitivityAnalysis, instrument, parallelShift: shiftPct, target: StressTestTarget.Price };
                const flt: StressTestFiltersDto = { ...filters, byUniqueId: false, uniqueId: null };
                setConfig(cfg);
                setFilters(flt);
                setActive('stress');
                void run(cfg, flt);
              }}
            />
          )}
        </div>
      </div>

      <ConfirmDialog
        show={confirmSnap}
        title="Are You Sure?"
        message="Are you sure you want to proceed with this action?"
        confirmLabel="Yes"
        confirmVariant="primary"
        busy={busy}
        onConfirm={() => { setConfirmSnap(false); void doTakeSnapshot(); }}
        onCancel={() => setConfirmSnap(false)}
      />
    </div>
  );
}

function StressContent({ filters, setFilters, config, setConfig, result, stressed, run, refreshOrRerun, busy, canCcy, canUid }: {
  filters: StressTestFiltersDto; setFilters: React.Dispatch<React.SetStateAction<StressTestFiltersDto>>;
  config: StressTestConfigurationDto; setConfig: React.Dispatch<React.SetStateAction<StressTestConfigurationDto>>;
  result: StressTestResultDto | null; stressed: boolean; run: (cfg?: StressTestConfigurationDto, flt?: StressTestFiltersDto) => void; refreshOrRerun: (flt: StressTestFiltersDto) => void; busy: boolean; canCcy: boolean; canUid: boolean;
}) {
  const [sub, setSub] = useState('clients');
  const [scenarioOpen, setScenarioOpen] = useState(false);
  const [loModal, setLoModal] = useState<{ login: number; connector: number } | null>(null);
  const portfolios = useQuery({ queryKey: ['rd-filter', 'portfolios'], queryFn: filterDataApi.getPortfolios, staleTime: 5 * 60_000 });
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000 });
  const quick = useQuery({ queryKey: ['nst-quick'], queryFn: nopStressTestApi.getQuickConfigurations, staleTime: 5 * 60_000 });

  const ccy = filters.ccyCurrency;
  const section: NopSectionDto | null = result
    ? (sub === 'clients' ? (ccy ? result.clientsNopCcy : result.clientsNop)
      : sub === 'firm' ? (ccy ? result.firmNopCcy : result.firmNop)
      : sub === 'lp' ? (ccy ? result.lpNopCcy : result.lpNop)
      : (ccy ? result.rtNopCcy : result.rtNop))
    : null;
  const stopOuts: LoginStopOutResultDto[] = result
    ? (sub === 'clients' ? result.stopOutResult?.nonCcyClients : sub === 'firm' ? result.stopOutResult?.nonCcyFirm : sub === 'lp' ? result.stopOutResult?.nonCcyLp : result.stopOutResult?.nonCcyReciptionAndTransmission) ?? []
    : [];
  const negBalTotal = stopOuts.reduce((a, r) => a + (Number(r.negativeBalance) || 0), 0);
  const stopOutCount = stopOuts.filter((r) => r.status && r.status !== '-').length;

  const str = (v: string | number | null | undefined) => (v == null ? '' : String(v));
  const t = config.type;
  const pfLocked = filters.portfolioId != null;
  const subTabs: TabItem[] = [{ key: 'clients', title: 'Clients NOP' }, { key: 'firm', title: 'Firm NOP' }, { key: 'lp', title: 'LP NOP' }, { key: 'rt', title: 'R&T NOP' }];

  const stopCols: DataGridColumn<LoginStopOutResultDto>[] = [
    {
      key: 'login',
      title: 'Login',
      value: (r) => r.login,
      cell: (r) =>
        r.login != null && r.platformId != null
          ? <a href="#" className="login-overview-link" onClick={(e) => { e.preventDefault(); setLoModal({ login: Number(r.login), connector: r.platformId }); }}>{r.login}</a>
          : (r.login ?? ''),
    },
    { key: 'platform', title: 'Connector', value: (r) => r.platform },
    { key: 'usableFunds', title: 'Original Equity', className: 'text-end', value: (r) => r.usableFunds, cell: (r) => money(r.usableFunds) },
    { key: 'stressedEquity', title: 'Stressed Equity', className: 'text-end', value: (r) => r.stressedEquity, cell: (r) => money(r.stressedEquity) },
    { key: 'pnl', title: 'Stressed Floating PnL', className: 'text-end', value: (r) => r.pnl, cell: (r) => money(r.pnl) },
    { key: 'originalPnl', title: 'Original Floating PnL', className: 'text-end', value: (r) => r.originalPnl, cell: (r) => money(r.originalPnl) },
    { key: 'pnlDifference', title: 'PnL Difference', className: 'text-end', value: (r) => r.pnlDifference, cell: (r) => money(r.pnlDifference) },
    { key: 'negativeBalance', title: 'Negative Balance', className: 'text-end', value: (r) => r.negativeBalance, cell: (r) => money(r.negativeBalance) },
    { key: 'creditDeficit', title: 'Credit Used', className: 'text-end', value: (r) => r.creditDeficit, cell: (r) => money(r.creditDeficit) },
    { key: 'status', title: 'Stop Out', value: (r) => r.status, cell: (r) => (r.status && r.status !== '-' ? <span className="badge bg-danger">{r.status}</span> : <span className="text-muted">-</span>) },
  ];

  const showShift = t === StressTestType.ParallelShift || t === StressTestType.SensitivityAnalysis;

  return (
    <>
      {/* Config + filters section (bordered). Config first: Stress Type + Run + presets. */}
      <div className="p-2 border-bottom">
        <div className="row g-2 align-items-end">
          <div className="col-lg-2 col-md-4">
            <Select2 label="Stress Test Type" searchable={false} allowClear placeholder="None" value={t === StressTestType.None ? '' : String(t)}
              options={[{ value: String(StressTestType.ParallelShift), label: 'Parallel Shift' }, { value: String(StressTestType.SensitivityAnalysis), label: 'Sensitivity Analysis' }, { value: String(StressTestType.ScenarioAnalysis), label: 'Scenario Analysis' }]}
              onChange={(v) => setConfig((c) => ({ ...c, type: v ? Number(v) : 0 }))} />
          </div>
          {t === StressTestType.SensitivityAnalysis && (
            <div className="col-lg-2 col-md-4"><label className="form-label">Instrument</label><Select2Async placeholder="Select" value={str(config.instrument) || null} fetch={filterDataPaged.instruments} onChange={(v) => setConfig((c) => ({ ...c, instrument: v || null }))} /></div>
          )}
          {showShift && (
            <>
              <div className="col-lg-2 col-md-3"><label className="form-label">Shift %</label><input className="form-control" inputMode="decimal" value={config.parallelShift ?? ''} onChange={(e) => setConfig((c) => ({ ...c, parallelShift: e.target.value === '' ? null : Number(e.target.value) }))} /></div>
              <div className="col-lg-2 col-md-3"><Select2 label="Parameter" searchable={false} allowClear={false} value={String(config.target)} options={[{ value: String(StressTestTarget.Price), label: 'Price' }, { value: String(StressTestTarget.Volume), label: 'Volume' }]} onChange={(v) => setConfig((c) => ({ ...c, target: Number(v) || 0 }))} /></div>
            </>
          )}
          {t === StressTestType.ScenarioAnalysis && (
            <div className="col-auto"><label className="form-label d-block">&nbsp;</label><button type="button" className="btn btn-outline-primary" onClick={() => setScenarioOpen(true)}><i className="fa fa-sliders me-1" />Scenario Analysis Config</button></div>
          )}
          <div className="col-auto"><label className="form-label d-block">&nbsp;</label><button type="button" className="btn btn-primary" onClick={() => run()} disabled={busy}>{busy ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-bolt me-1" />}Stress Test</button></div>
          {!!(quick.data?.length) && quick.data!.filter((q) => q.isActive).sort((a, b) => a.displayOrder - b.displayOrder).map((q) => (
            <div className="col-auto" key={q.id}><label className="form-label d-block">&nbsp;</label><button type="button" className={`btn btn-outline-${Number(q.percentage) < 0 ? 'danger' : 'primary'}`} onClick={() => { const cfg = { ...emptyConfig(), type: StressTestType.ParallelShift, parallelShift: Number(q.percentage) }; setConfig(cfg); run(cfg); }}>{q.name}</button></div>
          ))}
        </div>

        {/* Main filters row — compact inputs with the larger switches + Filter button alongside. */}
        <div className="row g-2 align-items-end mt-1">
          <div className="col-lg-2 col-md-3">
            <label className="form-label">{filters.byUniqueId ? 'Unique ID' : 'Login'}</label>
            {filters.byUniqueId
              ? <input className="form-control" value={filters.uniqueId ?? ''} onChange={(e) => setFilters((f) => ({ ...f, uniqueId: e.target.value || null }))} />
              : <input className="form-control" inputMode="numeric" disabled={pfLocked} value={filters.login ?? ''} onChange={(e) => setFilters((f) => ({ ...f, login: e.target.value ? Number(e.target.value) : null }))} />}
          </div>
          {!filters.byUniqueId && <div className="col-lg-2 col-md-3"><Select2 label="Connector" allowClear placeholder="All" disabled={pfLocked} value={str(filters.platformId)} options={connectors.data ?? []} onChange={(v) => setFilters((f) => ({ ...f, platformId: v ? Number(v) : null }))} /></div>}
          <div className="col-lg-2 col-md-3"><label className="form-label">Instrument</label><Select2Async placeholder="All" value={str(filters.instrument) || null} fetch={filterDataPaged.instruments} onChange={(v) => setFilters((f) => ({ ...f, instrument: v || null }))} /></div>
          {!filters.byUniqueId && <div className="col-lg-2 col-md-3"><Select2 label="Portfolio Profile" allowClear placeholder="All" value={str(filters.portfolioId)} options={portfolios.data ?? []} onChange={(v) => setFilters((f) => ({ ...f, portfolioId: v ? Number(v) : null }))} /></div>}
          <div className="col d-flex align-items-center gap-4 flex-wrap pb-1">
            {canCcy && <Switch label="CCY" on={filters.ccyCurrency} onToggle={(c) => { const nf = { ...filters, ccyCurrency: c }; setFilters(nf); refreshOrRerun(nf); }} />}
            {canUid && <Switch label="By Unique ID" on={filters.byUniqueId} onToggle={(c) => { const nf = { ...filters, byUniqueId: c, login: null, uniqueId: null, platformId: null, portfolioId: null }; setFilters(nf); refreshOrRerun(nf); }} />}
            <Switch label="Auto Map" on={filters.autoMap} onToggle={(c) => { const nf = { ...filters, autoMap: c }; setFilters(nf); refreshOrRerun(nf); }} />
            <button type="button" className="btn btn-primary ms-auto" onClick={() => refreshOrRerun(filters)} disabled={busy}><i className="fa fa-search me-1" />Filter</button>
          </div>
        </div>
      </div>

      {/* Statistics — only after a stress run. */}
      {stressed && (
        <div className="p-2 border-bottom">
          <div className="row g-2">
            <StatWidget2 label="Stress Test Type" value={TYPE_LABEL[t] ?? 'N/A'} icon="fe fe-activity" theme="neutral" />
            <StatWidget2 label="Total Stressed Floating PNL" value={money(section?.totalFloatingPnl, 3)} icon="ri-money-dollar-box-line" theme="warning" />
            <StatWidget2 label="Total PNL Difference" value={money(section?.totalPnlDifference, 3)} icon="ri-money-dollar-box-line" theme="warning" />
            <StatWidget2 label="Total Negative Balance" value={money(negBalTotal)} icon="ri-money-dollar-box-line" theme="primary" onClick={scrollToStopOut} />
            <StatWidget2 label="Stop Outs" value={String(stopOutCount)} icon="fe fe-users" theme="danger" onClick={scrollToStopOut} />
          </div>
        </div>
      )}

      {/* Sub-tabs + tables */}
      <div className="px-2 pt-2"><Tabs tabs={subTabs} active={sub} onChange={setSub} variant="underline" /></div>
      <div className="p-2">
        <StressNopTable section={section} isCcy={ccy} excelName={`Stress ${sub}`} card={false} />
        {stressed && (
          <div className="pt-4" id={STOP_OUT_ANCHOR}>
            <h5 className="text-center mb-2">Accounts stress test</h5>
            <DataGrid columns={stopCols} data={stopOuts} rowKey={(r) => `${r.login}-${r.platformId}`} card={false} includeExcel excelFileName="Accounts Stress Test" rowClassName={(r) => (r.status && r.status !== '-' ? 'threshold-breach' : '')} emptyMessage="No data." />
          </div>
        )}
      </div>

      <ScenarioOffcanvas
        open={scenarioOpen}
        savedPrices={config.modifiedPrices ?? []}
        stressType={config.type}
        target={config.target}
        onClose={() => setScenarioOpen(false)}
        onSave={(prices) => { setConfig((c) => ({ ...c, type: StressTestType.ScenarioAnalysis, modifiedPrices: prices, modifiedVolumes: null })); setScenarioOpen(false); }}
      />

      {loModal && <LoginOverviewModal login={loModal.login} connector={loModal.connector} onClose={() => setLoModal(null)} />}
    </>
  );
}

// Stat widget wrapped in a responsive 5-per-row column (matches legacy row-cols-xl-5).
function StatWidget2({ label, value, icon, theme, onClick }: { label: string; value: string; icon: string; theme: 'neutral' | 'primary' | 'success' | 'danger' | 'warning'; onClick?: () => void }) {
  return (
    <div className={`col-xl col-md-4 col-sm-6${onClick ? ' clickable-card cursor-pointer' : ''}`} onClick={onClick} role={onClick ? 'button' : undefined}>
      <StatWidget label={label} value={value || 'N/A'} icon={icon} theme={theme} border={false} />
    </div>
  );
}

function ReverseStopOut({ snapshotReady, onOpenStressTest }: { snapshotReady: boolean; onOpenStressTest: (instrument: string, shiftPct: number | null) => void }) {
  const toast = useToast();
  const [login, setLogin] = useState<string | null>(null);
  const [instrument, setInstrument] = useState<string | null>(null);
  const [connectorId, setConnectorId] = useState<number | null>(null);
  const [result, setResult] = useState<ReverseStopOutResultDto | null>(null);
  const [busy, setBusy] = useState(false);
  const connectors = useQuery({ queryKey: ['rd-filter', 'connectors'], queryFn: filterDataApi.getEnabledConnectors, staleTime: 5 * 60_000 });

  const calculate = async () => {
    if (!snapshotReady) { toast.warning('No snapshot available. Please click Take Snapshot first.'); return; }
    if (!login) { toast.warning('Please select a login.'); return; }
    if (!instrument) { toast.warning('Please select an instrument.'); return; }
    setBusy(true);
    try { setResult(await nopStressTestApi.calculateReverseStopOut({ login: Number(login), instrument, connectorId })); }
    catch { setResult(null); toast.error('Failed to calculate reverse stop-out.'); } finally { setBusy(false); }
  };

  // Deep-link: open the Stress Testing tab pre-loaded with a Sensitivity Analysis
  // run against the priced instrument + the computed price-variation shift. Only
  // offered once a non-zero stop-out price has been computed (legacy gate).
  const canOpenStress = !!result && !!result.resultPrice && result.resultPrice !== 0;
  const openStress = () => {
    if (!result || !instrument) return;
    const shiftPct = result.priceVariationPercent != null ? Number(result.priceVariationPercent.toFixed(2)) : null;
    onOpenStressTest(instrument, shiftPct);
  };
  const ccy = result?.quoteCurrency ? ` ${result.quoteCurrency}` : '';

  const posCols: DataGridColumn<ReverseStopOutResultDto['positions'][number]>[] = [
    { key: 'login', title: 'Login', value: (r) => r.login },
    { key: 'symbol', title: 'Instrument', value: (r) => r.symbol },
    { key: 'volume', title: 'Volume', className: 'text-end', value: (r) => r.volume, cell: (r) => money(r.volume, 2) },
    { key: 'openPrice', title: 'Open Price', className: 'text-end', value: (r) => r.openPrice, cell: (r) => money(r.openPrice, r.digits || 5) },
    { key: 'type', title: 'Type', value: (r) => r.type },
    { key: 'currentPrice', title: 'Current Price', className: 'text-end', value: (r) => r.currentPrice, cell: (r) => money(r.currentPrice, r.digits || 5) },
    { key: 'originalFloating', title: 'Original Floating', className: 'text-end', value: (r) => r.originalFloating, cell: (r) => money(r.originalFloating) },
    { key: 'recalculatedFloating', title: 'Recalculated Floating', className: 'text-end', value: (r) => r.recalculatedFloating, cell: (r) => money(r.recalculatedFloating) },
    { key: 'floatingDifference', title: 'Floating Difference', className: 'text-end', value: (r) => r.floatingDifference, cell: (r) => money(r.floatingDifference) },
  ];

  return (
    <>
      <div className="p-2 border-bottom">
        <div className="row g-2 align-items-end">
          <div className="col-lg-2 col-md-3"><label className="form-label">Login</label><Select2Async placeholder="Select login" value={login} fetch={async (term, page) => { const r = await nopStressTestApi.getLoginsForReverseStopOut(term, (page - 1) * 20, 20); return { items: (r.items ?? []).map((x) => ({ value: String(x.value), text: x.text })), hasMore: (page - 1) * 20 + (r.items?.length ?? 0) < (r.totalCount ?? 0) }; }} onChange={(v) => setLogin(v || null)} /></div>
          <div className="col-lg-2 col-md-3"><label className="form-label">Instrument</label><Select2Async placeholder="Select" value={instrument} fetch={filterDataPaged.instruments} onChange={(v) => setInstrument(v || null)} /></div>
          <div className="col-lg-2 col-md-3"><Select2 label="Connector" allowClear placeholder="All" value={connectorId == null ? '' : String(connectorId)} options={connectors.data ?? []} onChange={(v) => setConnectorId(v ? Number(v) : null)} /></div>
          <div className="col-auto"><label className="form-label d-block">&nbsp;</label><button type="button" className="btn btn-primary" onClick={calculate} disabled={busy}>{busy ? <i className="fa fa-spinner fa-spin me-1" /> : <i className="fa fa-calculator me-1" />}Calculate</button></div>
        </div>
      </div>
      <div className="p-2 border-bottom">
        <div className="row g-2">
          <div className="col-xl col-md-4 col-sm-6 position-relative">
            <StatWidget label="Stop Out Price" value={result ? money(result.resultPrice, result.digits || 5) + ccy : 'N/A'} icon="ri-money-dollar-box-line" theme="primary" border={false} />
            {canOpenStress && (
              <span className="open-stress-test-link text-primary text-nowrap cursor-pointer position-absolute top-0 end-0 mt-4 me-3" role="button" onClick={openStress}>Stress Test</span>
            )}
          </div>
          <StatWidget2 label="Price Difference" value={result ? money(result.priceDifference, result.digits || 5) + ccy : 'N/A'} icon="fe fe-trending-up" theme="neutral" />
          <StatWidget2 label="Price Variation (%)" value={result ? money(result.priceVariationPercent, 2) + '%' : 'N/A'} icon="fa fa-percent" theme="success" />
        </div>
      </div>
      <div className="p-2">
        <DataGrid columns={posCols} data={result?.positions ?? []} rowKey={(r) => `${r.login}-${r.symbol}`} card={false} includeExcel excelFileName="Reverse Stop-Out" emptyMessage="No positions." />
      </div>
    </>
  );
}

function Switch({ label, on, onToggle }: { label: string; on: boolean; onToggle: (c: boolean) => void }) {
  const id = `nst-switch-${label.replace(/\s+/g, '')}`;
  return (
    <div className="form-check form-switch mb-0">
      <input className="form-check-input" type="checkbox" role="switch" id={id} checked={on} onChange={(e) => onToggle(e.target.checked)} />
      <label className="form-check-label small" htmlFor={id}>{label}</label>
    </div>
  );
}
